import {
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  OnInit,
  OnDestroy,
  HostListener,
  ViewChild,
} from '@angular/core';

import { FormsModule } from '@angular/forms';
import * as fromSharedUi from '@sandbox-platform/shared-ui';
import {
  SearchInputComponent,
  BlurredBackdropComponent,
} from '@sandbox-platform/shared-ui';
import { ExpertDTO } from '../../types/expert.types';
import { SearchExpertModalStore } from './store/search-expert-modal.store';
import { SearchExpertNoRecordComponent } from './components/search-expert-no-record.component';
import { SearchExpertNoFilteredRecordComponent } from './components/search-expert-no-filtered-record.component';

@Component({
  standalone: true,
  imports: [
    FormsModule,
    SearchInputComponent,
    fromSharedUi.ErrorAlertComponent,
    fromSharedUi.CloseIconButtonComponent,
    fromSharedUi.BouncingLoaderComponent,
    fromSharedUi.BlurredBackdropComponent,
    SearchExpertNoRecordComponent,
    SearchExpertNoFilteredRecordComponent
],
  selector: 'lib-search-expert-modal',
  template: `
    @if (isOpen()) {
    <div class="fixed inset-0 z-50">
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <!-- Backdrop -->
        <shared-blurred-backdrop
          (backdropClick)="onCancel()"
          (escapeKey)="onCancel()"
        />

        <!-- Modal Content -->
        <span class="hidden sm:inline-block sm:align-middle sm:h-screen"
          >&#8203;</span
        >
        <div
          class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 fadeIn h-[600px]"
          (click)="$event.stopPropagation()"
        >
          <div class="h-full flex flex-col">
            <!-- Header -->
            <div class="mb-4 flex justify-between" (click)="focusSearchInput()">
              <h3 class="text-lg leading-6 font-medium text-gray-300">
                Select Expert
              </h3>

              <lib-close-icon-button
                size="sm"
                variant="modal"
                title="Clear expert filter"
                ariaLabel="Clear expert filter"
                (clicked)="onCancel()"
              />
            </div>

            <!-- Search Input -->
            <div class="mb-4">
              <lib-search-input
                #searchInput
                [searchTerm]="searchExpertModalStore.searchTerm()"
                placeholder="Search by name, email, or code..."
                label=""
                inputId="expert-search"
                (searchTermChange)="onSearchChange($event)"
              />
            </div>

            <!-- Error Message -->
            @if (searchExpertModalStore.error()) {
            <div class="mb-4">
              <lib-error-alert
                [error]="searchExpertModalStore.error()!"
                (dismiss)="searchExpertModalStore.clearError()"
              />
            </div>
            }

            <!-- Loading State -->
            @if (searchExpertModalStore.isLoading()) {
            <div class="mb-4">
              <lib-bouncing-loader
                [message]="'Loading experts...'"
                [ballColor]="'bg-orange-500'"
              />
            </div>
            }

            <!-- Expert List Table -->
            @if (!searchExpertModalStore.isLoading() && hasFilteredExperts()) {
            <div
              class="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden flex-1 flex flex-col"
            >
              <div class="overflow-y-auto flex-1">
                <table
                  class="min-w-[1000px] sm:min-w-full divide-y divide-gray-200 text-gray-700 text-sm"
                >
                  <thead class="font-semibold bg-slate-600 text-slate-100">
                    <tr>
                      <th scope="col" class="p-3 text-left">Name</th>
                      <th scope="col" class="p-3 text-left">Industry</th>
                      <th scope="col" class="p-3 text-left">Organization</th>
                      <th scope="col" class="p-3 text-left">Position</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    @for (expert of filteredExperts(); track expert.id) {
                    <tr
                      class="hover:bg-gray-50 transition-colors duration-150 text-sm cursor-pointer"
                      (click)="selectExpert(expert)"
                      [class.bg-blue-50]="selectedExpert()?.id === expert.id"
                    >
                      <td class="p-3 whitespace-nowrap">
                        {{ expert.full_name }}
                      </td>
                      <td class="p-3 whitespace-nowrap">
                        {{ expert.industry_name }}
                      </td>
                      <td class="p-3 whitespace-nowrap">
                        @if (expert.organization_name) {
                        <div class="text-sm text-gray-700">
                          {{ expert.organization_name }}
                        </div>
                        } @else {
                        <div class="text-sm text-gray-700">--</div>
                        }
                      </td>
                      <td class="p-3 whitespace-nowrap">
                        {{ expert.position }}
                      </td>
                    </tr>
                    }
                  </tbody>
                </table>
              </div>
            </div>
            }

            <!-- No Results Message -->
            @if (!searchExpertModalStore.isLoading() && !hasFilteredExperts() &&
            searchExpertModalStore.searchTerm()) {
            <div class="flex-1 flex items-center justify-center">
              <lib-search-expert-no-filtered-record />
            </div>
            }

            <!-- Empty State -->
            @if (!searchExpertModalStore.isLoading() && !hasFilteredExperts() &&
            !searchExpertModalStore.searchTerm()) {
            <div class="flex-1 flex items-center justify-center">
              <lib-search-expert-no-record />
            </div>
            }
          </div>
        </div>
      </div>
    </div>
    }
  `,
})
export class SearchExpertModalComponent implements OnInit, OnDestroy {
  @ViewChild('searchInput') searchInputRef!: SearchInputComponent;

  // Injected services
  searchExpertModalStore = inject(SearchExpertModalStore);

  // Inputs
  isOpen = input<boolean>(false);

  // Outputs
  expertSelected = output<ExpertDTO>();
  cancelled = output<void>();

  // Component state
  selectedExpert = signal<ExpertDTO | null>(null);

  // Computed properties
  filteredExperts = computed(() => {
    const experts = this.searchExpertModalStore.activeExperts();
    const searchTerm = this.searchExpertModalStore
      .searchTerm()
      .toLowerCase()
      .trim();

    if (!searchTerm) {
      // Return first 20 experts when no search term
      return this.searchExpertModalStore.initialExperts();
    }

    return experts.filter(
      (expert: ExpertDTO) =>
        expert.full_name.toLowerCase().includes(searchTerm) ||
        expert.first_name.toLowerCase().includes(searchTerm) ||
        expert.last_name.toLowerCase().includes(searchTerm) ||
        expert.email.toLowerCase().includes(searchTerm) ||
        expert.expert_id.toLowerCase().includes(searchTerm) ||
        expert.industry_name.toLowerCase().includes(searchTerm)
    );
  });

  hasFilteredExperts = computed(() => this.filteredExperts().length > 0);

  constructor() {
    // Load experts when modal opens
    effect(() => {
      if (this.isOpen()) {
        this.searchExpertModalStore.loadExperts();
        this.selectedExpert.set(null);

        setTimeout(() => {
          this.focusSearchInput();
        }, 500);
      }
    });
  }

  ngOnInit(): void {
    // Initial load if needed
    if (this.isOpen()) {
      this.searchExpertModalStore.loadExperts();
    }
  }

  ngOnDestroy(): void {
    // Clear search state when component is destroyed
    this.searchExpertModalStore.clearSearch();
  }

  onSearchChange(searchTerm: string): void {
    this.searchExpertModalStore.setSearchTerm(searchTerm);
    this.selectedExpert.set(null); // Clear selection when search changes
  }

  selectExpert(expert: ExpertDTO): void {
    this.selectedExpert.set(expert);
    this.expertSelected.emit(expert);
  }

  focusSearchInput(): void {
    this.searchInputRef.focusAndSelectAll();
  }

  onConfirm(event: Event): void {
    event.stopPropagation();
    const expert = this.selectedExpert();
    if (expert) {
      this.expertSelected.emit(expert);
    }
  }

  onCancel(): void {
    this.selectedExpert.set(null);
    this.searchExpertModalStore.clearSearch();
    this.cancelled.emit();
  }

  getExpertInitials(expert: ExpertDTO): string {
    const firstInitial = expert.first_name.charAt(0).toUpperCase();
    const lastInitial = expert.last_name.charAt(0).toUpperCase();
    return `${firstInitial}${lastInitial}`;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onKeydownHandler(event: Event) {
    if (this.isOpen()) {
      this.onCancel();
    }
  }
}
